import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { createColumnHelper } from '@tanstack/react-table'
import { Button, RadioInput, Table, TextInput } from '@demandlocal/components'
import { useGetPaginatedDealerVaultDealerships } from '../../../../api/admin/useGetPaginatedDealerVaultDealerships'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import { useModal } from '../../../../components/useModal'
import AdminUpdateDealerVault from '../../../../forms/Admin/AdminUpdateDealerVault'
import { useGetLastUpdatedDealerVaultPartners } from '../../../../api/admin/partners/useGetLastUpdatedDealerVaultPartners'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'

type DealerVaultCol = {
  dealer_id: string
  program_name: string
  group_name: string
  dealer_name: string
  dms: string
  file_type: string
  feed_status: string
  deactivated_by: string
  deactivated_date: string
  initial_delivery: string
  last_successful_delivery: string
  record_count: number
  max_content_date: string
}

const columnHelper = createColumnHelper<DealerVaultCol>()

export default function DealerVault(): JSX.Element {
  const { data: last_udpated_data } = useGetLastUpdatedDealerVaultPartners()
  const [filters, setFilters] = useState<Partial<DealerVaultCol>>({})
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])

  const { handleModal } = useModal()
  const columns = useMemo(
    () => [
      columnHelper.accessor('dealer_id', {
        header: () => 'ID',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('group_name', {
        header: () => 'Group',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('dealer_name', {
        header: () => 'Dealership',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('dms', {
        header: () => 'DMS',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('file_type', {
        header: () => 'File Types',
        cell: (info) => (JSON.parse(info.getValue()) ?? []).join(', '),
        enableSorting: false,
      }),
      columnHelper.accessor('feed_status', {
        header: () => 'Feed Status',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('record_count', {
        header: () => 'Record Count',
        cell: (info) => formatNumber(info.getValue()),
      }),
      columnHelper.accessor('initial_delivery', {
        header: () => 'Initial Delivery',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      columnHelper.accessor('last_successful_delivery', {
        header: () => 'Last Successful Delivery',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      columnHelper.accessor('max_content_date', {
        header: () => 'Max Content Date',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      columnHelper.accessor('deactivated_by', {
        header: () => 'Deactivated By',
        cell: (info) => info.getValue() ?? 'N/A',
        enableSorting: false,
      }),
      columnHelper.accessor('deactivated_date', {
        header: () => 'Deactivated Date',
        cell: (info) => info.getValue() ?? 'N/A',
        enableSorting: false,
      }),
    ],
    [],
  )

  return (
    <motion.div className="flex flex-col gap-2 pt-5">
      <div className="flex justify-between">
        <OutPortal node={filtersPortalNode} />

        <div className="flex justify-end items-center gap-3">
          <span className="font-bold text-xs">
            Last Updated: {last_udpated_data?.data.last_updated}
          </span>
          <Button onClick={() => handleModal(<AdminUpdateDealerVault />)}>Update</Button>
        </div>
      </div>
      <FiltersContext portalNode={filtersPortalNode} setFilters={setFilters}>
        <TextInput name="dealer_id" placeholder="Dealer ID" label="Dealer ID" />
        <TextInput name="dealership" placeholder="Dealership" label="Dealership" />
        <RadioInput
          options={[
            { label: 'Complete', value: 'Complete' },
            { label: 'Deactivated', value: 'Deactivated' },
          ]}
          name={'feed_status'}
          label="Feed Status"
        />
      </FiltersContext>
      <Table
        columns={columns}
        filters={filters}
        fetchFn={useGetPaginatedDealerVaultDealerships}
      />
    </motion.div>
  )
}
